import React, { useEffect, useRef, useState } from 'react';
import { OptImg } from '../ui/OptImg';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith('/api/') ? `${API_URL}${u}` : u);
const strip = (h) => (h || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
const canHover = () => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

// Tile photos for categories whose catalog image looks like its neighbours (same bright living room)
const STORE = `${API_URL}/api/storage/files/apollo-shades`;
const TILE = {
  drapery: `${STORE}/cms/ai-generated/batch/c882e9e9-923e-4d01-982a-952ffb99bbee/e2909b79-63bd-474f-a26b-9e4ac21acac9.webp`,
  motorization: `${STORE}/apollo-shades/cms/ai-generated/motorized-shades/0f65aace-4ba0-4de0-b8f5-5e52795a8929.webp`,
};

// Existing destinations — unchanged
const CARDS = [
  { id: 'shades', index: '01', name: 'Custom Shades', to: '/products/shades', source: { category: 'shades' } },
  { id: 'roman', index: '02', name: 'Roman Shades', to: '/products/roman-shades', source: { product: 'roman-shades' } },
  { id: 'drapery', index: '03', name: 'Drapery', to: '/products/drapery', source: { category: 'drapery' }, tileImage: TILE.drapery },
];
const MOTOR = { id: 'motorization', index: '04', name: 'Motorization', to: '/motorization', source: { category: 'motorization' }, tileImage: TILE.motorization };

const useCardContent = (cards) => {
  const [data, setData] = useState({});
  useEffect(() => {
    Promise.all([axios.get(`${API_URL}/api/cms/categories`), axios.get(`${API_URL}/api/cms/products`)]).then(([c, p]) => {
      const out = {};
      cards.forEach((card) => {
        if (!card.source) return;
        if (card.source.category) {
          const cat = c.data.find((x) => x.slug === card.source.category);
          if (cat) out[card.id] = { image: abs(cat.image_url), description: strip(cat.description) };
        } else {
          const prod = p.data.find((x) => x.product_type === card.source.product);
          if (prod) out[card.id] = { image: abs(prod.images?.[0]?.url || prod.image_url), description: strip(prod.description || prod.short_description) };
        }
      });
      setData(out);
    }).catch(() => {});
  }, []);
  return data;
};

const FLIP_STYLE = (flipped) => ({ transform: flipped ? 'rotateX(-180deg)' : 'rotateX(0deg)', transition: 'transform 690ms cubic-bezier(0.22, 1, 0.36, 1)', transformStyle: 'preserve-3d', willChange: 'transform' });

// One flip card: minimal front (name only), back reveals image + description + Read More
const FlipCard = ({ card, content, flipped, onEnter, onTap, onKey, wide, compact }) => {
  const turned = useRef(false);
  if (flipped) turned.current = true;
  const turnCls = turned.current ? (flipped ? 'flip-turn-open' : 'flip-turn-close') : '';
  return (
  <div
    role="button"
    tabIndex={0}
    aria-expanded={flipped}
    aria-label={`${card.name} — ${flipped ? 'showing details' : 'press Enter to show details'}`}
    onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onKey(); } }}
    className={`group relative [perspective:1400px] outline-none focus-visible:ring-2 focus-visible:ring-amber-600 focus-visible:ring-offset-4 rounded-[2px] ${wide ? 'h-[230px] md:h-[230px]' : compact ? 'h-[260px] md:h-[330px]' : 'h-[290px] md:h-[400px]'}`}
    onMouseEnter={onEnter}
    onClick={onTap}
    data-testid={`flip-card-${card.id}`}
    data-flipped={flipped ? 'true' : 'false'}
  >
    <div className={`relative h-full w-full rounded-[2px] ${turnCls}`} style={FLIP_STYLE(flipped)}>
      {/* Front */}
      <div aria-hidden={flipped} className={`absolute inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden] overflow-hidden rounded-[2px] ${wide ? 'bg-neutral-950 text-white' : 'bg-[#f6f2eb] text-neutral-900 border border-[#e7dfd2]'}`}>
        <span className="flip-shade" />
        <div className={`absolute inset-0 pointer-events-none ${wide ? 'bg-[radial-gradient(ellipse_at_80%_50%,rgba(180,83,9,0.18),transparent_60%)]' : 'bg-[radial-gradient(ellipse_at_50%_0%,rgba(255,255,255,0.9),transparent_70%)]'}`} />
        <span className={`absolute top-6 left-7 font-mono text-[11px] tracking-[0.3em] ${wide ? 'text-amber-500/80' : 'text-amber-700/70'}`}>{card.index}</span>
        <div className={`relative h-full flex ${wide ? 'items-center justify-between px-10 md:px-16' : 'flex-col items-center justify-center text-center px-8'}`}>
          <div className={wide ? '' : 'flex flex-col items-center'}>
            <h3 className={`font-serif font-light tracking-tight ${wide ? 'text-4xl md:text-6xl' : compact ? 'text-[2.1rem] md:text-[1.85rem] lg:text-[2.2rem] leading-[1.08]' : 'text-[2.6rem] md:text-[3.15rem] leading-[1.05]'}`} data-testid={`flip-card-${card.id}-front-title`}>{card.name}</h3>
            <span className={`block h-px bg-amber-600 transition-all duration-500 ${wide ? 'w-16 mt-5' : 'w-10 mt-6 group-hover:w-16'}`} />
          </div>
          {wide && <span className="hidden md:block text-[11px] uppercase tracking-[0.35em] text-white/40">Smart control</span>}
        </div>
        <span className={`absolute bottom-6 ${wide ? 'right-10 md:right-16' : 'left-1/2 -translate-x-1/2'} whitespace-nowrap uppercase tracking-[0.3em] ${wide ? 'text-white/45' : 'text-neutral-500'}`}>
          <span className="hidden md:inline text-[10.5px]">Hover to explore</span><span className={`md:hidden text-[11.5px] font-medium ${wide ? 'text-white/60' : 'text-neutral-600'}`}>Tap to explore</span>
        </span>
      </div>
      {/* Back */}
      <div aria-hidden={!flipped} className="absolute inset-0 [backface-visibility:hidden] [-webkit-backface-visibility:hidden] [transform:rotateX(180deg)] overflow-hidden rounded-[2px] bg-neutral-900 text-white">
        <span className="flip-shade" />
        {(card.tileImage || content?.image || card.image) && <OptImg src={card.tileImage || content?.image || card.image} alt={card.name} className="absolute inset-0 h-full w-full object-cover" sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw" loading={Number(card.index) <= 4 ? undefined : 'lazy'} />}
        <div className={`absolute inset-0 ${wide ? 'bg-gradient-to-r from-black/85 via-black/55 to-black/10' : 'bg-gradient-to-t from-black/90 via-black/45 to-black/5'}`} />
        <div className={`relative h-full flex flex-col ${wide ? 'justify-center px-6 md:px-16 max-w-2xl' : compact ? 'justify-end p-5 md:p-6' : 'justify-end p-6 md:p-8'}`}>
          <span className="font-mono text-[11px] tracking-[0.3em] text-amber-400/90 mb-2 md:mb-3">{card.index}</span>
          <h3 className={`font-serif font-light ${wide ? 'text-2xl md:text-4xl' : compact ? 'text-xl md:text-2xl' : 'text-2xl md:text-3xl'}`}>{card.name}</h3>
          <p className="mt-2 md:mt-3 text-[13px] md:text-sm leading-relaxed text-white/80 line-clamp-3 md:line-clamp-4" data-testid={`flip-card-${card.id}-description`}>{content?.description || card.description}</p>
          {card.to ? <Link to={card.to} onClick={(e) => e.stopPropagation()} tabIndex={flipped ? 0 : -1}
            className="mt-4 md:mt-6 inline-flex w-fit items-center gap-2 border-b border-amber-500 pb-1 text-xs font-medium uppercase tracking-[0.25em] text-white transition-colors hover:text-amber-300"
            data-testid={`flip-card-${card.id}-read-more`}>
            Read More <ArrowRight className="w-3.5 h-3.5" />
          </Link> : <span className="mt-4 md:mt-6 text-[11px] uppercase tracking-[0.25em] text-white/50" data-testid={`flip-card-${card.id}-coming-soon`}>Page coming soon</span>}
        </div>
      </div>
    </div>
  </div>
  );
};

export const ProductCategoryFlipCards = () => {
  const content = useCardContent([...CARDS, MOTOR]);
  const [active, setActive] = useState(null);
  const enter = (id) => () => { if (canHover()) setActive(id); };
  const tap = (id) => () => { if (!canHover()) setActive((a) => (a === id ? null : id)); };
  const key = (id) => () => setActive((a) => (a === id ? null : id));
  const ref = useRef(null);
  useEffect(() => {
    // Phones: floating chat/WhatsApp buttons step aside while the category cards are on screen
    const el = ref.current;
    if (!el || !window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver(([e]) => document.body.classList.toggle('floating-hidden-mobile', e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => { io.disconnect(); document.body.classList.remove('floating-hidden-mobile'); };
  }, []);
  return (
    <section ref={ref} className="mb-14" onMouseLeave={() => canHover() && setActive(null)} data-testid="product-category-flip-cards">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {CARDS.map((c) => <FlipCard key={c.id} card={c} content={content[c.id]} flipped={active === c.id} onEnter={enter(c.id)} onTap={tap(c.id)} onKey={key(c.id)} />)}
      </div>
      <div className="mt-5 md:mt-6">
        <FlipCard wide card={MOTOR} content={content[MOTOR.id]} flipped={active === MOTOR.id} onEnter={enter(MOTOR.id)} onTap={tap(MOTOR.id)} onKey={key(MOTOR.id)} />
      </div>
    </section>
  );
};

const AI = (h) => `https://static.prod-images.emergentagent.com/jobs/40af1b99-2067-4f0a-9458-756e0f39b6b0/images/${h}.jpeg`;

// 8-card layout (4 per row). Cards without `to` have no destination page yet.
export const GRID_CARDS = [
  { id: 'shades', index: '01', name: 'Shades', to: '/products/shades', source: { category: 'shades' } },
  { id: 'blinds', index: '02', name: 'Blinds', to: '/products/blinds', image: AI('4d534a0248fae332caf9cafbd47fd72b7b8865a0c9c94af5d924fc90e8732c8a'), description: 'Wood, faux wood, aluminum and vertical blinds tailored to every window, offering precise light control with a timeless, architectural line.' },
  { id: 'drapery', index: '03', name: 'Drapery', to: '/products/drapery', source: { category: 'drapery' }, tileImage: TILE.drapery },
  { id: 'fabric-swatches', index: '04', name: 'Fabric Swatches', to: '/fabrics', image: AI('2c543b3370d04e876fbf8e8f8824ab03edabb2217cfcef630354bb129509aab3'), description: 'Explore our full fabric library: textures, colors and opacities for shades and drapery, ready to sample at home.' },
  { id: 'residential', index: '05', name: 'Residential', to: '/products/residential', image: AI('b3ee8e07218f4c5b975a44cbeab3cd450ddc69e0cfaff4d8b5eb2c100a2e1350'), description: 'Custom window treatments designed for comfort, privacy, light control, and seamless integration into the home.' },
  { id: 'commercial', index: '06', name: 'Commercial Grade', to: '/products/commercial', image: AI('31b311440ab77beeb03534a2fe1d03b8659e7608d1373c6afd9a841dd67b8a58'), description: 'Durable shading solutions engineered for offices, hospitality, multifamily, and other demanding commercial environments.' },
  { id: 'architectural', index: '07', name: 'Architectural', to: '/products/architectural', image: AI('bd61682c6ba8f672a9044d51a021156deb798269f298dc0a30f84009b50727a6'), description: 'Specification-driven shading systems for architects, designers, and complex projects requiring precise performance and integration.' },
  { id: 'motorized', index: '08', name: 'Motorized Systems', to: '/motorization', source: { category: 'motorization' }, tileImage: TILE.motorization },
];

export const ProductFlipGrid = () => {
  const content = useCardContent(GRID_CARDS);
  const [active, setActive] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver(([e]) => document.body.classList.toggle('floating-hidden-mobile', e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => { io.disconnect(); document.body.classList.remove('floating-hidden-mobile'); };
  }, []);
  return (
    <section ref={ref} className="mb-14" onMouseLeave={() => canHover() && setActive(null)} data-testid="product-flip-grid">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {GRID_CARDS.map((c) => (
          <FlipCard key={c.id} compact card={c} content={content[c.id]} flipped={active === c.id}
            onEnter={() => canHover() && setActive(c.id)}
            onTap={() => !canHover() && setActive((a) => (a === c.id ? null : c.id))}
            onKey={() => setActive((a) => (a === c.id ? null : c.id))} />
        ))}
      </div>
    </section>
  );
};
